/**
 * parseExtractoBancario · lee un extracto bancario Interbanco / BAC
 * (CSV o XLSX) y devuelve movimientos normalizados.
 *
 * Interbanco (formato estándar de descarga):
 *   Fecha | Descripción | Referencia | Débito | Crédito | Saldo
 *
 * BAC:
 *   Fecha | Referencia | Descripción | Débitos | Créditos | Saldo
 *
 * "OTRO" · si el archivo no matchea, permite mapeo manual de columnas.
 *
 * Task #145.
 */

export type MovimientoParseado = {
  fecha: string            // YYYY-MM-DD
  tipo: 'CREDITO' | 'DEBITO'
  monto: number
  descripcion: string | null
  referencia: string | null
  saldo_despues: number | null
}

export type BancoOrigen = 'INTERBANCO' | 'BAC' | 'OTRO'

// Diccionario de aliases por banco. Case-insensitive. Se buscan por INCLUYE, no exacto.
const ALIASES: Record<BancoOrigen, Record<keyof MovimientoParseado | 'debito' | 'credito', string[]>> = {
  INTERBANCO: {
    fecha:          ['fecha', 'fecha operacion', 'fecha de operación'],
    tipo:           [], // deriva de debito/credito
    monto:          [],
    descripcion:    ['descripcion', 'descripción', 'concepto', 'detalle'],
    referencia:     ['referencia', 'no. documento', 'documento', 'no doc'],
    saldo_despues:  ['saldo', 'saldo actual', 'saldo final'],
    debito:         ['debito', 'débito', 'debitos', 'débitos', 'cargo', 'egreso'],
    credito:        ['credito', 'crédito', 'creditos', 'créditos', 'abono', 'ingreso'],
  },
  BAC: {
    fecha:          ['fecha'],
    tipo:           [],
    monto:          [],
    descripcion:    ['descripcion', 'descripción', 'concepto', 'detalle', 'oficina'],
    referencia:     ['referencia', 'documento', 'no. transaccion', 'no. transacción'],
    saldo_despues:  ['saldo', 'balance'],
    debito:         ['debito', 'débito', 'debitos', 'débitos'],
    credito:        ['credito', 'crédito', 'creditos', 'créditos'],
  },
  OTRO: {
    fecha:          ['fecha', 'date'],
    tipo:           [],
    monto:          ['monto', 'amount'],
    descripcion:    ['descripcion', 'descripción', 'concepto', 'detalle', 'description'],
    referencia:     ['referencia', 'ref', 'documento', 'reference'],
    saldo_despues:  ['saldo', 'balance'],
    debito:         ['debito', 'débito', 'debit'],
    credito:        ['credito', 'crédito', 'credit'],
  },
}

const norm = (s: string) => (s ?? '').toString().toLowerCase().trim().replace(/\s+/g, ' ')

/** Busca en `headers` la primer columna que contenga alguno de `candidates`. */
function findCol(headers: string[], candidates: string[]): number {
  for (let i = 0; i < headers.length; i++) {
    const h = norm(headers[i])
    if (candidates.some(c => h.includes(norm(c)))) return i
  }
  return -1
}

function parseNumero(v: any): number | null {
  if (v === null || v === undefined || v === '') return null
  if (typeof v === 'number') return v
  // Quitar Q, comas, espacios, paréntesis (negativos)
  let s = String(v).trim().replace(/[Q\s,]/g, '')
  const neg = s.startsWith('(') && s.endsWith(')')
  s = s.replace(/[()]/g, '')
  const n = parseFloat(s)
  if (Number.isNaN(n)) return null
  return neg ? -n : n
}

function parseFecha(v: any): string | null {
  if (!v) return null
  // Si viene como Date (Excel)
  if (v instanceof Date) return v.toISOString().slice(0, 10)
  const s = String(v).trim()
  // DD/MM/YYYY o DD-MM-YYYY
  const m1 = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/)
  if (m1) {
    const [, d, m, y] = m1
    const yyyy = y.length === 2 ? `20${y}` : y
    return `${yyyy}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
  }
  // YYYY-MM-DD
  const m2 = s.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/)
  if (m2) {
    const [, y, m, d] = m2
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
  }
  return null
}

export type ParseResult = {
  movimientos: MovimientoParseado[]
  headers: string[]
  descartados: number
  advertencias: string[]
}

/** Parsea un ArrayBuffer (XLSX/CSV/TSV) a movimientos normalizados. */
export async function parseExtractoBancario(
  buffer: ArrayBuffer,
  banco: BancoOrigen = 'INTERBANCO',
): Promise<ParseResult> {
  const XLSX = await import('xlsx')
  const wb = XLSX.read(buffer, { type: 'array', cellDates: true })
  const sheetName = wb.SheetNames[0]
  const sheet = wb.Sheets[sheetName]
  const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: '' })

  const advertencias: string[] = []
  if (rows.length === 0) return { movimientos: [], headers: [], descartados: 0, advertencias: ['Archivo vacío'] }

  // Buscar la fila de headers REAL: la que tiene "fecha" Y una columna de importe
  // (débito/crédito/monto). Los extractos BAC traen un bloque "Encabezado" arriba
  // con una fila "Fecha | <serial>" que NO es el header de transacciones — por eso
  // exigimos también la columna de importe para no agarrar esa.
  const tieneFecha = (joined: string) => joined.includes('fecha') || joined.includes('date')
  const tieneImporte = (joined: string) => /d[eé]bito|cr[eé]dito|monto|amount|abono|cargo/.test(joined)

  let headerRow = -1
  for (let i = 0; i < Math.min(rows.length, 25); i++) {
    const nonEmpty = rows[i].filter(c => c !== '' && c !== null && c !== undefined).length
    if (nonEmpty < 3) continue
    const joined = rows[i].map(norm).join('|')
    if (tieneFecha(joined) && tieneImporte(joined)) { headerRow = i; break }
  }
  // Fallback (formatos raros): la primera fila con "fecha".
  if (headerRow === -1) {
    for (let i = 0; i < Math.min(rows.length, 25); i++) {
      const nonEmpty = rows[i].filter(c => c !== '' && c !== null && c !== undefined).length
      if (nonEmpty < 3) continue
      if (tieneFecha(rows[i].map(norm).join('|'))) { headerRow = i; break }
    }
  }
  if (headerRow === -1) {
    return { movimientos: [], headers: [], descartados: 0, advertencias: ['No se detectó la fila de encabezados (busco "fecha")'] }
  }

  const headers = rows[headerRow].map((h: any) => String(h ?? ''))
  const dict = ALIASES[banco]

  const colFecha       = findCol(headers, dict.fecha)
  const colDesc        = findCol(headers, dict.descripcion)
  const colRef         = findCol(headers, dict.referencia)
  const colSaldo       = findCol(headers, dict.saldo_despues)
  const colDebito      = findCol(headers, dict.debito)
  const colCredito     = findCol(headers, dict.credito)
  const colMonto       = banco === 'OTRO' ? findCol(headers, dict.monto) : -1

  if (colFecha === -1) advertencias.push('No encontré columna Fecha')
  if (colDebito === -1 && colCredito === -1 && colMonto === -1) {
    advertencias.push('No encontré columnas Débito/Crédito ni Monto')
  }

  const movimientos: MovimientoParseado[] = []
  let descartados = 0

  for (let i = headerRow + 1; i < rows.length; i++) {
    const r = rows[i]
    if (!r || r.every(c => c === '' || c === null)) continue

    const fecha = parseFecha(r[colFecha])
    if (!fecha) { descartados++; continue }

    let tipo: 'CREDITO' | 'DEBITO' | null = null
    let monto: number | null = null

    if (colCredito >= 0 && colDebito >= 0) {
      const cred = parseNumero(r[colCredito])
      const deb = parseNumero(r[colDebito])
      if (cred && cred > 0) { tipo = 'CREDITO'; monto = cred }
      else if (deb && deb > 0) { tipo = 'DEBITO'; monto = deb }
    } else if (colMonto >= 0) {
      const m = parseNumero(r[colMonto])
      if (m !== null) {
        tipo = m >= 0 ? 'CREDITO' : 'DEBITO'
        monto = Math.abs(m)
      }
    }

    if (!tipo || !monto) { descartados++; continue }

    movimientos.push({
      fecha,
      tipo,
      monto,
      descripcion: colDesc >= 0 ? String(r[colDesc] ?? '').trim() || null : null,
      referencia: colRef >= 0 ? String(r[colRef] ?? '').trim() || null : null,
      saldo_despues: colSaldo >= 0 ? parseNumero(r[colSaldo]) : null,
    })
  }

  return { movimientos, headers, descartados, advertencias }
}
